import { graphql } from "relay-runtime";
import { parseRoute } from "src/wac-routes";

import { anchorPageRelayQuery$data } from "@/__generated_contentful__/anchorPageRelayQuery.graphql";
import { AnchorContentsResponse } from "@/utils/anchor-queries/get-anchor-contents";
import { WAC_TO_CPAPI_LOCALE_MAP } from "@/utils/content-queries/constants";

import { richTextToHTML } from "../../clients/uniform-client/enhancers/utils";

/**
 * Contentful Anchors query (used for all anchor pages)
 */
export const AnchorPageRelayQuery = graphql`
    query anchorPageRelayQuery($locale: String!, $paidVariant: String!) {
        anchorCollection(
            locale: $locale
            where: { paidVariant: $paidVariant }
            limit: 1
            skip: 0
        ) {
            items {
                anchorTitleExternal
                paidVariant
                personaCollection(
                    limit: 1
                    where: { personaNameExternal: "unknown" }
                ) {
                    items {
                        personaNameExternal
                    }
                }
                productCollection(limit: 1) {
                    items {
                        productNameReference {
                            productNameId
                        }
                    }
                }
                headlineCollection(limit: 1) {
                    items {
                        headlineTitleExternal
                        headlineSubheading
                        planBenefitsCollection(limit: 1) {
                            items {
                                planBenefits {
                                    json
                                    links {
                                        entries {
                                            inline {
                                                sys {
                                                    id
                                                }
                                            }
                                        }
                                    }
                                }
                                supportingAsset {
                                    url
                                    description
                                }
                                planFeaturesCollection(limit: 6) {
                                    items {
                                        description {
                                            json
                                            links {
                                                entries {
                                                    inline {
                                                        sys {
                                                            id
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                primaryMessageCollection(limit: 1) {
                    items {
                        primaryMessageTitleExternal
                        supportingExampleCollection(limit: 10) {
                            items {
                                proofpoint {
                                    json
                                }
                                supportingAsset {
                                    url
                                    description
                                }
                                supportingExampleHeading
                                supportingExampleTitleExternal
                                advocateQuote {
                                    advocateHeadshot {
                                        url
                                        description
                                    }
                                    advocateName
                                    advocateQuote
                                    advocateJobTitle
                                }
                            }
                        }
                    }
                }
                bannerCollection(limit: 3) {
                    items {
                        bannerUrl
                        bannerImage {
                            url
                            description
                        }
                        bannerBackground {
                            url
                        }
                        bannerText {
                            json
                        }
                        bannerUrlText
                        bannerUrl
                    }
                }
                resultsCollection(limit: 10) {
                    items {
                        sys {
                            id
                        }
                        resultTitleExternal
                        lozenge
                        newResultSubheading {
                            json
                        }
                        resultAsset {
                            title
                            fileName
                            url
                            description
                            contentType
                            title
                        }
                    }
                }
                callToActionCollection(limit: 1) {
                    items {
                        valueProposition
                        ctaText
                        ctaUrl
                    }
                }
                socialProofCollection(limit: 1) {
                    items {
                        socialProofTitleExternal
                        customersCollection(limit: 16) {
                            items {
                                logoCollection(limit: 1) {
                                    items {
                                        url
                                        description
                                    }
                                }
                                altDarkLogoCollection(limit: 1) {
                                    items {
                                        url
                                        description
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`;

/**
 * Returns variables for Contentful Anchor Pages query
 */
export const getAnchorPageRelayQueryVars = (
    cmsPageVariant: string,
    location: Location
) => {
    const { locale } = parseRoute(location);

    return {
        paidVariant: cmsPageVariant,
        locale: WAC_TO_CPAPI_LOCALE_MAP[locale] || "en-US"
    };
};

/**
 * Transforms Contentful Response to shape matching original CPAPI response/props
 */
export function shapeAnchorContentfulResponse(
    data: anchorPageRelayQuery$data
): AnchorContentsResponse {
    // eslint-disable-next-line complexity
    return data.anchorCollection?.items.map((anchor) => ({
        node: {
            name: anchor?.anchorTitleExternal || "",
            pageVariant: anchor?.paidVariant || "paid-lp-jsw-features",
            persona:
                anchor?.personaCollection?.items.map((persona) => ({
                    name: persona?.personaNameExternal || "",
                    id: persona?.personaNameExternal || ""
                })) || [],
            product:
                anchor?.productCollection?.items.map((product) => ({
                    productNameId:
                        product?.productNameReference?.productNameId || ""
                })) || [],
            headline:
                anchor?.headlineCollection?.items.map((headline) => ({
                    name: headline?.headlineTitleExternal || null,
                    subheading: headline?.headlineSubheading || null,
                    planBenefits:
                        headline?.planBenefitsCollection?.items.map(
                            (planBenefit) => ({
                                description: richTextToHTML(
                                    planBenefit?.planBenefits?.json
                                ),
                                supportingAsset: {
                                    url:
                                        planBenefit?.supportingAsset?.url || "",
                                    description:
                                        planBenefit?.supportingAsset
                                            ?.description || ""
                                },
                                planFeatures:
                                    planBenefit?.planFeaturesCollection?.items.map(
                                        (planFeature) => ({
                                            description: richTextToHTML(
                                                planFeature?.description?.json
                                            )
                                        })
                                    ) || null
                            })
                        ) || []
                })) || [],
            primaryMessage:
                anchor?.primaryMessageCollection?.items.map(
                    (primaryMessage) => ({
                        name: primaryMessage?.primaryMessageTitleExternal || "",
                        supportingExample:
                            primaryMessage?.supportingExampleCollection?.items.map(
                                // eslint-disable-next-line complexity
                                (supportingExample) => ({
                                    proofpoint: richTextToHTML(
                                        supportingExample?.proofpoint?.json
                                    ),
                                    supportingAsset: {
                                        url:
                                            supportingExample?.supportingAsset
                                                ?.url || "",
                                        description:
                                            supportingExample?.supportingAsset
                                                ?.description || ""
                                    },
                                    heading:
                                        supportingExample?.supportingExampleHeading ||
                                        null,
                                    name:
                                        supportingExample?.supportingExampleTitleExternal ||
                                        "",
                                    advocateQuote:
                                        !!supportingExample?.advocateQuote
                                            ? {
                                                  advocateHeadshot: {
                                                      url:
                                                          supportingExample
                                                              ?.advocateQuote
                                                              ?.advocateHeadshot
                                                              ?.url || null,
                                                      description:
                                                          supportingExample
                                                              ?.advocateQuote
                                                              ?.advocateHeadshot
                                                              ?.description ||
                                                          null
                                                  },
                                                  advocateName:
                                                      supportingExample
                                                          ?.advocateQuote
                                                          ?.advocateName ||
                                                      null,
                                                  advocateQuote:
                                                      supportingExample
                                                          ?.advocateQuote
                                                          ?.advocateQuote ||
                                                      null,
                                                  advocateJobTitle:
                                                      supportingExample
                                                          ?.advocateQuote
                                                          ?.advocateJobTitle ||
                                                      null
                                              }
                                            : null
                                })
                            ) || []
                    })
                ) || [],
            banner:
                // eslint-disable-next-line complexity
                anchor?.bannerCollection?.items.map((banner) => ({
                    bannerImage: {
                        description: banner?.bannerImage?.description || null,
                        url: banner?.bannerImage?.url || null
                    },
                    backgroundImage: {
                        url: banner?.bannerBackground?.url || null
                    },
                    text: richTextToHTML(banner?.bannerText?.json) || "",
                    url: banner?.bannerUrl || "",
                    urlText: banner?.bannerUrlText || ""
                })) || null,
            results:
                // eslint-disable-next-line complexity
                anchor?.resultsCollection?.items.map((result) => ({
                    id: result?.sys?.id || "",
                    name: result?.resultTitleExternal || "",
                    lozenge: result?.lozenge || null,
                    subheading: richTextToHTML(
                        result?.newResultSubheading?.json
                    ),
                    asset: {
                        url: result?.resultAsset?.url || "",
                        description: result?.resultAsset?.description || "",
                        fileName: result?.resultAsset?.fileName || "",
                        title: result?.resultAsset?.title || "",
                        details: null,
                        contentType: result?.resultAsset?.contentType || ""
                    }
                })) || [],
            socialProof:
                anchor?.socialProofCollection?.items.map((proof) => ({
                    name: proof?.socialProofTitleExternal || "",
                    customers:
                        proof?.customersCollection?.items.map((customer) => ({
                            logo:
                                customer?.logoCollection?.items.map((logo) => ({
                                    url: logo?.url || "",
                                    description: logo?.description || ""
                                })) || [],
                            altDarkLogo:
                                customer?.altDarkLogoCollection?.items.map(
                                    (darkLogo) => ({
                                        url: darkLogo?.url || "",
                                        description: darkLogo?.description || ""
                                    })
                                ) || []
                        })) || []
                })) || [],
            callToAction:
                anchor?.callToActionCollection?.items.map((cta) => ({
                    valueProposition: cta?.valueProposition || "",
                    text: cta?.ctaText || "",
                    url: cta?.ctaUrl || ""
                })) || []
        }
    }));
}
